import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

// Re-export Section for backward compatibility
export type { Section };

export type CustomerFeatureFlagDefinitionSchema = BaseFeatureFlagDefinition & {
  section?: Section;
};

export const CustomerFeatureFlagDataZod = zod.object({
  classifyStep: zod.boolean().default(false),
  locateStep: zod.boolean().default(false),
  ros2: zod.boolean().default(true),
  legacyHaasSteps: zod.boolean().default(true),
  enableCodeBlocks: zod.boolean().default(false),
  pythonExpressions: zod.boolean().default(false),
  etherNetIP: zod.boolean().default(true),
  darkMode: zod.boolean().default(false),
});

export type CustomerFeatureFlagData = zod.infer<
  typeof CustomerFeatureFlagDataZod
>;
export type CustomerFeatureFlagKey = keyof CustomerFeatureFlagData;

export const customerFeatureFlagDefinitions: Record<
  CustomerFeatureFlagKey,
  CustomerFeatureFlagDefinitionSchema
> = {
  classifyStep: {
    title: 'Classify Step',
    dateAdded: '2023-06-08',
    section: 'vision',
    description: 'Classify/Categorize what the camera sees',
    allowSingleUser: true,
  },
  ros2: {
    title: 'Enable ROS2',
    dateAdded: '2025-06-26',
    description: 'Enable ROS2 motions and migrations',
    section: 'movement',
    allowSingleUser: false,
  },
  locateStep: {
    title: 'Locate Step',
    dateAdded: '2023-07-20',
    section: 'vision',
    description: 'Find objects in 3D space',
    allowSingleUser: true,
  },
  legacyHaasSteps: {
    title: 'Legacy Haas steps',
    dateAdded: '2024-03-29',
    description:
      'Include the legacy Haas steps "Monitor Haas machine" and "Run Haas program" in the step library',
    section: 'equipment',
    allowSingleUser: true,
  },
  enableCodeBlocks: {
    title: 'Enable Code Blocks',
    dateAdded: '2024-08-10',
    description: 'Enable Code Blocks',
    section: 'infra',
  },
  pythonExpressions: {
    requiresRestart: false,
    title: 'Python Expressions',
    dateAdded: '2024-10-15',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable python expressions',
  },
  etherNetIP: {
    title: 'EtherNet/IP',
    dateAdded: '2025-01-29',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable EtherNet/IP adapter integration',
  },
  darkMode: {
    requiresRestart: false,
    title: 'Dark mode',
    dateAdded: '2023-11-21',
    section: 'infra',
    allowSingleUser: true,
    description: 'Enable dark mode UI',
  },
};

type GroupedKeys = { section: Section; keys: CustomerFeatureFlagKey[] }[];

export const customerFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(customerFeatureFlagDefinitions)) {
    const key = keyStr as CustomerFeatureFlagKey;
    let { section } = customerFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
